import React, { useCallback, useReducer, useRef } from 'react';
import classnames from 'classnames';
import {
  assign,
  debounce,
  isEmpty,
  kebabCase,
  map,
  max,
  min,
  set,
} from 'lodash/fp';
import fetchJSON from '../_utils/fetchJSON';

const mapWithKey = map.convert({ cap: false });

const keysToHandle = ['Enter', 'Escape', 'ArrowDown', 'ArrowUp'];

const SearchBox = ({
  searchResultsEndpoint,
  searchParams = {},
  searchSuggestEndpoint,
  isHeaderSearch,
  defaultValue,
  heading,
  placeholder = 'Type in a keyword…',
}) => {
  const formRef = useRef(null);
  const inputRef = useRef(null);
  const suggestionsListRef = useRef(null);

  const initialState = {
    queryTerm: defaultValue || '',
    selectedSuggestionIndex: -1,
    showSuggestions: false,
    suggestions: [],
  };

  const setStateFromKeyDown = ({ state, payload, initialState }) => {
    let newIndex;
    switch (payload) {
      case 'Enter':
        formRef.current.submit();
        return assign(state, {
          selectedSuggestionIndex: initialState.selectedSuggestionIndex,
          showSuggestions: false,
        });
      case 'Escape':
        inputRef.current.focus();
        return assign(state, initialState);
      case 'ArrowDown':
        newIndex = min([
          state.selectedSuggestionIndex + 1,
          state.suggestions.length - 1,
        ]);
        if (newIndex < state.suggestions.length) {
          suggestionsListRef.current.children[newIndex].firstChild.focus();
        }
        return state.showSuggestions
          ? assign(state, {
              queryTerm: state.suggestions[newIndex] || state.queryTerm,
              selectedSuggestionIndex: newIndex,
            })
          : state;
      case 'ArrowUp':
        newIndex = max([0, state.selectedSuggestionIndex - 1]);
        if (newIndex > -1) {
          suggestionsListRef.current.children[newIndex].firstChild.focus();
        }
        return state.showSuggestions
          ? assign(state, {
              queryTerm: state.suggestions[newIndex] || state.queryTerm,
              selectedSuggestionIndex: newIndex,
            })
          : state;
      default:
        return state;
    }
  };

  function searchBoxReducer(state, { type, payload }) {
    switch (type) {
      case 'selectSuggestion':
        formRef.current.submit();
        return assign(state, {
          queryTerm: state.suggestions[payload],
          selectedSuggestionIndex: payload,
          showSuggestions: false,
        });
      case 'updateQueryTerm':
        return set('queryTerm', payload, state);
      case 'updateSuggestions':
        return assign(state, {
          selectedSuggestionIndex: initialState.selectedSuggestionIndex,
          showSuggestions: !isEmpty(state.queryTerm),
          suggestions: payload,
        });
      case 'updateSelectedSuggestion':
        return assign(state, {
          queryTerm: state.suggestions[payload],
          selectedSuggestionIndex: payload,
        });
      case 'keyDown':
        return setStateFromKeyDown({ state, payload, initialState });
      default:
        return state;
    }
  }

  const [state, dispatch] = useReducer(searchBoxReducer, initialState);

  const searchBoxClasses = classnames('search-box', {
    'search-box--header': isHeaderSearch,
  });

  const headingClasses = classnames('search-box__heading', {
    'search-box__heading--header': isHeaderSearch,
  });

  const formClasses = classnames('search-box__form', {
    'search-box__form--header': isHeaderSearch,
  });

  const suggestionsClasses = classnames('suggestions', {
    active:
      state.showSuggestions && state.suggestions && state.suggestions.length,
  });

  const getSuggestions = useCallback(
    debounce(300, function (value) {
      fetchJSON(
        `${searchSuggestEndpoint}&partial_query=${encodeURIComponent(value)}`
      )
        .then((suggestions) =>
          dispatch({
            type: 'updateSuggestions',
            payload: suggestions || [],
          })
        )
        .catch(() =>
          dispatch({
            type: 'updateSuggestions',
            payload: [],
          })
        );
    }),
    []
  );

  const handleQueryChange = (evt) => {
    const { value } = evt.target;
    dispatch({
      type: 'updateQueryTerm',
      payload: value,
    });

    if (value) {
      getSuggestions(value);
    } else {
      dispatch({
        type: 'updateSuggestions',
        payload: [],
      });
    }
  };

  const handleSelectSuggestion = (evt, idx) => {
    evt.preventDefault();
    dispatch({
      type: 'selectSuggestion',
      payload: idx,
    });
  };

  const handleFocusSuggestion = (idx) => {
    dispatch({
      type: 'updateSelectedSuggestion',
      payload: idx,
    });
  };

  const handleSearchBoxKeyDown = useCallback(
    (evt) => {
      evt.persist();

      if (state.showSuggestions && keysToHandle.includes(evt.key)) {
        evt.preventDefault();
        dispatch({
          type: 'keyDown',
          payload: evt.key,
        });
      }
    },
    [state]
  );

  const renderHiddenInputs = (
    <>
      {mapWithKey(
        (val, key) => (
          <input key={key} type="hidden" name={key} value={val} />
        ),
        searchParams
      )}
    </>
  );

  const renderedSuggestions = isEmpty(state.suggestions)
    ? null
    : mapWithKey((suggestion, idx) => {
        const btnClasses = classnames('suggestions__btn', {
          active: idx === state.selectedSuggestionIndex,
        });
        return (
          <li
            key={`suggestion-${idx}-${kebabCase(suggestion)}`}
            className="suggestions__item"
          >
            <button
              className={btnClasses}
              onClick={(evt) => handleSelectSuggestion(evt, idx)}
              onFocus={() => handleFocusSuggestion(idx)}
              aria-label={`Change search term to "${suggestion}"`}
            >
              {suggestion}
            </button>
          </li>
        );
      }, state.suggestions);

  return (
    <div className={searchBoxClasses}>
      <h2 className={headingClasses}>{heading}</h2>
      <form
        ref={formRef}
        className={formClasses}
        action={searchResultsEndpoint}
        onKeyDown={handleSearchBoxKeyDown}
      >
        {renderHiddenInputs}
        <input
          ref={inputRef}
          type="search"
          className="search-box__input"
          name="query"
          placeholder={placeholder}
          autoComplete="off"
          onChange={handleQueryChange}
          aria-label="Search term"
          value={state.queryTerm}
        />
        <button type="submit" className="search-box__submit">
          <span className="search-box__submit-label">Search</span>
        </button>
        <div className={suggestionsClasses}>
          <ul ref={suggestionsListRef} className="suggestions__list">
            {renderedSuggestions}
          </ul>
        </div>
      </form>
    </div>
  );
};

export default SearchBox;
